import type { ClauseFact, ClauseKind } from '../../types/document'
import { truncate, type Segment } from './text'

// Agreements, loan papers and insurance policies: find the clauses a person
// should read before signing, each with the exact sentence and its key figure
// (months, %, ₹) when the sentence has one. Nothing is judged or summarised.

const MONEY = /(?:₹|rs\.?|inr)\s*[\d,]+(?:\.\d+)?(?:\s*\/-)?|[\d,]+(?:\.\d+)?\s*(?:rupees|रुपये|रुपए)/i
const PERCENT = /\d+(?:\.\d+)?\s*%(?:\s*(?:p\.?\s*a\.?|per\s*(?:annum|month|year)))?/i
const DURATION = /\b\d+\s*(?:days?|months?|years?|weeks?)\b|\b(?:one|two|three|four|six|eleven|twelve|thirty|sixty|ninety)\s*(?:\(\d+\)\s*)?(?:days?|months?|years?)\b/i

type Pick = 'money' | 'percent' | 'duration'
const RULES: Array<{ kind: ClauseKind; re: RegExp; pick: Pick[]; max?: number }> = [
  { kind: 'lock_in', re: /lock[- ]?in/i, pick: ['duration'] },
  { kind: 'notice_period', re: /notice\s*period|\bdays?'?\s*(?:prior\s*)?(?:written\s*)?notice|months?'?\s*(?:prior\s*)?(?:written\s*)?notice|notice\s*of\s*\d+|नोटिस\s*अवधि/i, pick: ['duration'] },
  { kind: 'security_deposit', re: /security\s*deposit|refundable\s*deposit|interest[- ]free\s*deposit|advance\s*(?:rent|deposit)|जमानत\s*राशि|सिक्योरिटी\s*डिपॉजिट|अनामत/i, pick: ['money'] },
  { kind: 'rent_increase', re: /(?:increase|enhance|escalat|revis|hike)\w*[^.]{0,50}(?:rent|licen[cs]e\s*fee)|(?:rent|licen[cs]e\s*fee)[^.]{0,50}(?:increase|enhance|escalat|hike)|किराए?\s*में\s*(?:वृद्धि|बढ़ोतरी)/i, pick: ['percent', 'money'] },
  { kind: 'late_fee', re: /late\s*(?:payment\s*)?(?:fee|charge)|penal\s*(?:interest|charge)|penalty|delayed\s*payment|overdue\s*(?:charge|interest)|bounce\s*charge|विलंब\s*शुल्क|जुर्माना/i, pick: ['money', 'percent'] },
  { kind: 'auto_renewal', re: /auto(?:matic(?:ally)?)?[- ]?renew|renewed\s*automatically|shall\s*(?:be\s*)?(?:stand\s*)?renewed/i, pick: ['duration'] },
  { kind: 'termination', re: /terminat|vacate|eviction|forfeit|cancel(?:led|lation)?\s*(?:of\s*)?(?:the\s*)?(?:agreement|policy|loan)/i, pick: ['duration', 'money'] },
  { kind: 'interest_rate', re: /rate\s*of\s*interest|interest\s*rate|\broi\b|annual\s*percentage\s*rate|\bapr\b|ब्याज\s*दर/i, pick: ['percent'] },
  { kind: 'foreclosure', re: /foreclos|pre-?\s*payment|pre-?\s*closure|part[- ]?payment\s*charge/i, pick: ['percent', 'money'] },
  { kind: 'processing_fee', re: /processing\s*(?:fee|charge)|documentation\s*charge|login\s*fee|administrative\s*charge|प्रोसेसिंग\s*फीस/i, pick: ['money', 'percent'] },
  { kind: 'waiting_period', re: /waiting\s*period|प्रतीक्षा\s*अवधि/i, pick: ['duration'] },
  { kind: 'exclusions', re: /exclusions?\b|not\s*(?:be\s*)?covered|shall\s*not\s*(?:be\s*)?(?:liable|payable|covered)|excluded/i, pick: [], max: 3 },
  { kind: 'maintenance', re: /maintenance\s*(?:charge|amount|fee)|society\s*charges|electricity\s*and\s*water|water\s*and\s*electricity|minor\s*repairs|major\s*repairs/i, pick: ['money'] },
  { kind: 'jurisdiction', re: /jurisdiction|arbitration|arbitrator|न्यायक्षेत्र|मध्यस्थ/i, pick: [] }
]

function figure(text: string, pick: Pick[]): string {
  for (const p of pick) {
    const re = p === 'money' ? MONEY : p === 'percent' ? PERCENT : DURATION
    const m = text.match(re)
    if (m) return m[0].replace(/\s+/g, ' ').trim()
  }
  return ''
}

/** Clauses worth checking, in the order of RULES (most important first). */
export function extractClauses(segments: Segment[]): ClauseFact[] {
  const out: ClauseFact[] = []
  for (const rule of RULES) {
    const max = rule.max ?? 1
    const used = new Set<string>()
    for (const seg of segments) {
      if (used.size >= max) break
      if (!rule.re.test(seg.text) || seg.text.length < 12) continue
      const evidence = truncate(seg.text)
      if (used.has(evidence)) continue
      used.add(evidence)
      out.push({ kind: rule.kind, value: figure(seg.text, rule.pick), evidence, certainty: seg.certainty })
    }
  }
  return out
}

export const AGREEMENT_TYPES = ['rental_agreement', 'loan_agreement', 'insurance', 'contract'] as const
